import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getZoneContributions } from "@/queries/report";

interface FlightContributionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zoneId: number | null;
  interval: number | null;
  zoneName?: string;
  timeLabel?: string;
  measure?: string;
  zoneIds?: number[];
  zoneType?: string | null;
}

function gateLabelForModal(zoneName: string): string {
  const t = zoneName.trim();
  if (/^gate\s/i.test(t)) {
    return t;
  }
  return `Gate ${t}`;
}

export function FlightContributionsModal({
  open,
  onOpenChange,
  zoneId,
  interval,
  zoneName = "Zone",
  timeLabel = "",
  measure = "pax",
  zoneIds,
  zoneType,
}: FlightContributionsModalProps) {
  const { data: contributions, isLoading } = getZoneContributions(
    zoneId,
    interval,
    zoneIds
  );

  const suffixText =
    {
      pax: "Passengers",
      flight: "Flights",
      bag: "Bags",
      bags: "Bags",
    }[measure] ?? "Passengers";

  const total =
    contributions && Array.isArray(contributions)
      ? contributions.reduce(
          (sum: number, flight: any) => sum + flight.value,
          0
        )
      : 0;

  const internationalTotal =
    contributions && Array.isArray(contributions)
      ? contributions.reduce(
          (sum: number, flight: any) =>
            sum + (flight.flight.international ? flight.value : 0),
          0
        )
      : 0;

  const domesticTotal =
    contributions && Array.isArray(contributions)
      ? contributions.reduce(
          (sum: number, flight: any) =>
            sum + (!flight.flight.international ? flight.value : 0),
          0
        )
      : 0;

  const internationalPercentage =
    total > 0 ? (internationalTotal / total) * 100 : 0;
  const domesticPercentage = total > 0 ? (domesticTotal / total) * 100 : 0;

  const isGateLoungePax = zoneType === "gate" && measure === "pax";
  const dialogTitle = isGateLoungePax
    ? `${gateLabelForModal(zoneName)} Lounge Demand — ${timeLabel}`
    : `Flight Contributions - ${zoneName} @ ${timeLabel}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          {isGateLoungePax ? (
            <p className="text-sm font-medium text-slate-400 pt-1">
              Flights Driving This Demand
            </p>
          ) : null}
        </DialogHeader>
        <ScrollArea className="h-[calc(100vh-250px)]">
          {isLoading && <div>Loading...</div>}
          {contributions &&
          Array.isArray(contributions) &&
          contributions.length > 0 ? (
            <>
              <div>
                {contributions.map((contrib: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex justify-between items-start py-3 border-t border-slate-700/50"
                  >
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        {contrib.flight.carrier_image && (
                          <img
                            src={contrib.flight.carrier_image}
                            className="w-6 h-6"
                            onError={(e) =>
                              (e.currentTarget.style.display = "none")
                            }
                            alt=""
                          />
                        )}
                        <span className="font-semibold text-base">
                          {contrib.flight.number}
                        </span>
                        {(contrib.flight.status_oag || "")
                          .toLowerCase()
                          .includes("cancel") && (
                          <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 text-xs">
                            Canceled
                          </Badge>
                        )}
                        <Badge variant={"secondary"} className="text-xs">
                          {contrib.flight.international
                            ? "INTERNATIONAL"
                            : "DOMESTIC"}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-400">
                        {contrib.flight.dep_iata} → {contrib.flight.arr_iata}
                        {contrib.flight.gate && (
                          <span className="ml-2">
                            • Gate{" "}
                            <span className="font-medium">
                              {contrib.flight.gate}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="font-medium text-base">
                      {Math.round(contrib.value)} {suffixText}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-4 mt-2 border-t border-slate-700">
                <span className="font-bold text-lg">
                  {isGateLoungePax
                    ? `Added to Lounge at ${timeLabel}`
                    : "Total"}
                </span>
                <span className="font-bold text-lg">
                  {Math.round(total)} {suffixText}
                </span>
              </div>
              <div className="mt-4 space-y-2 pt-2 border-t border-slate-700/50">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">International</span>
                  <span className="font-medium">
                    {Math.round(internationalTotal)} {suffixText}
                    <span className="text-slate-400 ml-2">
                      ({internationalPercentage.toFixed(1)}%)
                    </span>
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Domestic</span>
                  <span className="font-medium">
                    {Math.round(domesticTotal)} {suffixText}
                    <span className="text-slate-400 ml-2">
                      ({domesticPercentage.toFixed(1)}%)
                    </span>
                  </span>
                </div>
              </div>
            </>
          ) : contributions &&
            Array.isArray(contributions) &&
            contributions.length === 0 ? (
            <div className="text-slate-400">
              No flight contributions for this interval
            </div>
          ) : null}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
